import { logger } from '../../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { isGeoJSONSource } from '../map-engine.js'
import {
  type CompassService,
  type LocationReading,
} from '../../../services/compass/compass-provider'

const log = logger.create('map')
import { circleToGeoJSONCoords } from '../geo-utils.js'
import type { ActivityType } from 'cairn-compass'

export interface LocationTrackingCallbacks {
  onLocationUpdated: (location: {
    lat: number
    lng: number
    heading: number | undefined
    accuracy: number | undefined
  }) => void
  onElevationUpdated: (elevation: number | undefined) => void
  onVelocityUpdated: (velocity: number | undefined) => void
  onHasLocationData: () => void
  onHeadingUpdated: (heading: number | undefined) => void
  onTrackPoint: (location: {
    latitude: number
    longitude: number
    altitude: number | undefined
    accuracy: number | undefined
    speed?: number | undefined
    activity?: ActivityType
    timestamp?: number
    buffered?: boolean
  }) => void
  storeLocation: (data: {
    lat: number
    lng: number
    zoom: number | undefined
    timestamp: number
    accuracy: number
    source: string
  }) => void
  getMapZoom: () => number | undefined
  getTrackQuality: () => 'high' | 'standard' | 'extended'
  isRecording: () => boolean
}

export class LocationTrackingManager {
  private map: maplibregl.Map | undefined
  private locationMarker: maplibregl.Marker | undefined
  private isLocationTracking = false
  private deviceHeading: number | undefined
  private accuracyCircleSourceId = 'accuracy-circle'
  private accuracyCircleLayerId = 'accuracy-circle-fill'
  private accuracyCircleStrokeLayerId = 'accuracy-circle-stroke'

  private static readonly DISPLACEMENT_MAP: Record<'high' | 'standard' | 'extended', number> = {
    high: 3,
    standard: 5,
    extended: 10,
  }

  private currentLocation:
    | { lat: number; lng: number; heading: number | undefined; accuracy: number | undefined }
    | undefined

  /**
   * Monotonic "unwrapped" heading used as the CSS rotate() value for the
   * compass arrow. Raw compass headings live in [0, 360), so rotating
   * from 10° → 350° would animate the long way around under
   * `transition: transform`. We track the continuous value by adding the
   * shortest angular delta on each update — crossing north goes
   * 10° → −10° instead of 10° → 350°, and the CSS interpolation takes
   * the short path.
   */
  private continuousHeading: number | undefined

  private boundLocationHandler: ((reading: LocationReading) => void) | undefined

  constructor(
    private callbacks: LocationTrackingCallbacks,
    private compassService: CompassService
  ) {}

  private boundPitchBearingHandler?: () => void

  setMap(map: maplibregl.Map): void {
    this.map = map
    // Update marker tilt when camera pitch/bearing changes. Use the
    // unwrapped continuous heading so the camera-driven re-render
    // doesn't snap the rotate() value back across the 0°/360° boundary.
    this.boundPitchBearingHandler = () => {
      if (this.locationMarker && this.currentLocation) {
        this.updateArrowMarkerElement(this.locationMarker, this.continuousHeading)
      }
    }
    this.map.on('pitch', this.boundPitchBearingHandler)
    this.map.on('rotate', this.boundPitchBearingHandler)
  }

  async startLocationTracking(): Promise<void> {
    if (this.isLocationTracking) {
      return
    }
    this.isLocationTracking = true // Set before await to prevent race condition with multiple callers

    try {
      log.debug('🚀 Starting professional foreground location tracking...')

      // Setup location update listener (bind once to avoid duplicate registrations)
      if (!this.boundLocationHandler) {
        this.boundLocationHandler = this.handleLocationUpdate.bind(this)
        this.compassService.onLocationUpdate(this.boundLocationHandler)
      }

      // Start foreground location service with user's track quality setting
      const displacement =
        LocationTrackingManager.DISPLACEMENT_MAP[this.callbacks.getTrackQuality()]
      this.compassService.logDiagnostic(
        'LOCATION',
        `startLocationTracking priority=high interval=5s displacement=${displacement}m`
      )
      await this.compassService.startLocationTracking({
        interval: 5, // 5s backstop for smooth map display
        priority: 'high', // GPS + GLONASS + Network fusion
        displacement, // User's track quality setting
        enableForegroundService: true,
      })
      log.debug('✅ Professional location tracking started with foreground service')
    } catch (error) {
      this.isLocationTracking = false // Reset on failure so retry is possible
      log.warn('Failed to start location tracking:', (error as Error).message ?? error)
    }
  }

  private handleLocationUpdate(location: LocationReading): void {
    log.debug(
      `📍 Location update: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)} ±${location.accuracy}m from ${location.provider}`
    )

    const locationData = {
      lat: location.latitude,
      lng: location.longitude,
      heading: this.deviceHeading, // Use compass heading, not GPS bearing
      accuracy: location.accuracy,
    }

    // Update elevation and velocity from high-accuracy GPS data
    this.callbacks.onElevationUpdated(location.altitude)
    this.callbacks.onVelocityUpdated(location.speed)

    // Mark that we have location data
    this.callbacks.onHasLocationData()

    // Update map marker
    this.updateLocationMarker(locationData)

    // Notify the component of the location update
    this.callbacks.onLocationUpdated(locationData)

    // Store location for persistence with detailed metadata
    this.callbacks.storeLocation({
      lat: locationData.lat,
      lng: locationData.lng,
      zoom: this.callbacks.getMapZoom(),
      timestamp: location.timestamp,
      accuracy: locationData.accuracy,
      source: 'gps', // Use 'gps' as required by LocationPersistenceService
    })

    // Record track point if recording is active
    const trackPoint: Parameters<LocationTrackingCallbacks['onTrackPoint']>[0] = {
      latitude: location.latitude,
      longitude: location.longitude,
      altitude: location.altitude,
      accuracy: location.accuracy,
      speed: location.speed,
      timestamp: location.timestamp,
      ...(location.buffered ? { buffered: true } : {}),
    }
    // Prefer the per-point activity stamped on the reading by the native plugin:
    // live broadcasts carry the current classification, and buffered points
    // carry the classification that was current when the fix was captured. Fall
    // back to the service's current activity only when the reading has none
    // (e.g. the web platform, or points written by an older buffer format).
    if (location.activity) {
      trackPoint.activity = location.activity
    } else {
      try {
        const activityState = this.compassService.getCurrentActivity()
        trackPoint.activity = activityState.activity
      } catch {
        // Activity state not yet initialized
      }
    }
    this.callbacks.onTrackPoint(trackPoint)
  }

  async startCompassTracking(): Promise<void> {
    try {
      // Subscribe to compass updates
      this.compassService.onHeadingUpdate(reading => {
        this.deviceHeading = reading.heading
        // Unwrap for CSS rotate — must run before every marker update so
        // the 0°/360° boundary doesn't animate the long way around.
        this.advanceContinuousHeading(reading.heading)

        // Update marker if we have a location
        if (this.currentLocation && this.locationMarker) {
          this.updateArrowMarkerElement(this.locationMarker, this.continuousHeading)
        }

        // Notify the component of heading update (normalized, not unwrapped)
        this.callbacks.onHeadingUpdated(reading.heading)

        log.debug(`Compass: ${reading.heading}° (±${reading.accuracy}°) via ${reading.source}`)
      })

      // Start compass tracking when app is visible
      if (document.visibilityState === 'visible') {
        log.debug('App is visible - starting compass tracking immediately')
        await this.compassService.startTracking()
      }
    } catch (error) {
      log.error('Failed to start compass tracking:', error)
    }
  }

  /**
   * Fold a raw [0, 360) heading into `continuousHeading` by adding the
   * shortest signed delta. Returns the updated continuous value.
   */
  private advanceContinuousHeading(rawHeading: number): number {
    if (this.continuousHeading === undefined) {
      this.continuousHeading = rawHeading
      return this.continuousHeading
    }
    // Normalize current continuous value into [0, 360) to measure delta
    // against the raw reading. JS `%` preserves sign for negatives, so
    // add 360 before the final modulo.
    const currentMod = ((this.continuousHeading % 360) + 360) % 360
    let delta = rawHeading - currentMod
    if (delta > 180) delta -= 360
    else if (delta < -180) delta += 360
    this.continuousHeading += delta
    return this.continuousHeading
  }

  private createArrowMarkerElement(heading?: number): HTMLElement {
    const rotation = heading ?? 0
    const el = document.createElement('div')
    el.className = 'current-location-marker'
    el.style.width = '40px'
    el.style.height = '40px'
    el.innerHTML = `
      <div class="compass-arrow" style="width:40px;height:40px;position:relative;transform:rotate(${rotation}deg);transform-origin:center center;transition:transform 600ms ease-out;">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <polygon points="20,5 30,28 20,22 20,5" fill="#5a7ba3" stroke="#2d3e56" stroke-width="0.8"/>
          <polygon points="20,5 20,22 10,28 20,5" fill="#3f5a7a" stroke="#2d3e56" stroke-width="0.8"/>
          <polygon points="10,28 20,22 30,28 20,26 10,28" fill="#2d3e56" stroke="#1e2936" stroke-width="0.8"/>
        </svg>
      </div>`
    return el
  }

  private updateArrowMarkerElement(marker: maplibregl.Marker, heading?: number): void {
    const rotation = heading ?? 0
    const arrow = marker.getElement().querySelector<HTMLElement>('.compass-arrow')
    if (!arrow) {
      return
    }

    const slopeTilt = this.computeSlopeTilt()
    if (slopeTilt) {
      // Map bearing rotates the whole scene — we need to account for it
      const mapBearing = this.map?.getBearing() ?? 0
      const mapPitch = this.map?.getPitch() ?? 0
      // Combine: first lay flat on terrain (using map pitch), then tilt by slope, then rotate for heading
      arrow.style.transform =
        `rotateX(${mapPitch}deg) ` +
        `rotateX(${slopeTilt.tiltX}deg) rotateZ(${slopeTilt.tiltZ}deg) ` +
        `rotate(${rotation - mapBearing}deg)`
    } else {
      arrow.style.transform = `rotate(${rotation}deg)`
    }
  }

  /**
   * Sample terrain elevation at 4 cardinal points around current location
   * and compute average slope tilt angles. Returns null if terrain is not active.
   */
  private computeSlopeTilt(): { tiltX: number; tiltZ: number } | null {
    if (!this.map || !this.currentLocation) {
      return null
    }
    const terrain = this.map.getTerrain()
    if (!terrain) {
      return null
    }

    const { lng, lat } = this.currentLocation
    // ~50m offset in degrees (rough approximation)
    const dLat = 0.00045
    const dLng = 0.00045 / Math.cos((lat * Math.PI) / 180)

    const elN = this.map.queryTerrainElevation({ lng, lat: lat + dLat })
    const elS = this.map.queryTerrainElevation({ lng, lat: lat - dLat })
    const elE = this.map.queryTerrainElevation({ lng: lng + dLng, lat })
    const elW = this.map.queryTerrainElevation({ lng: lng - dLng, lat })

    if (elN === null || elS === null || elE === null || elW === null) {
      return null
    }

    // Slope in north-south direction (positive = tilting north/uphill)
    const slopeNS = (Math.atan2(elN - elS, 100) * 180) / Math.PI // 100m = 2 * 50m
    // Slope in east-west direction
    const slopeEW = (Math.atan2(elE - elW, 100) * 180) / Math.PI

    // tiltX tilts around X axis (east-west axis) = north-south slope
    // tiltZ tilts around Z axis = not needed, we use rotateY for east-west
    // Actually: rotateX pitches forward/back, rotateZ rolls left/right in screen space
    // We need to rotate these slopes into screen space based on map bearing
    const bearingRad = (this.map.getBearing() * Math.PI) / 180
    const tiltX = slopeNS * Math.cos(bearingRad) + slopeEW * Math.sin(bearingRad)
    const tiltZ = -slopeNS * Math.sin(bearingRad) + slopeEW * Math.cos(bearingRad)

    return { tiltX, tiltZ }
  }

  private updateLocationMarker(location: {
    lat: number
    lng: number
    heading: number | undefined
    accuracy: number | undefined
  }): void {
    if (!this.map) {
      return
    }

    this.currentLocation = location
    const lngLat: [number, number] = [location.lng, location.lat]

    if (this.locationMarker) {
      this.locationMarker.setLngLat(lngLat)
      // Use the unwrapped continuous heading (not location.heading) so
      // a GPS fix arriving between compass updates doesn't reset the
      // rotate() value back across the 0°/360° boundary.
      this.updateArrowMarkerElement(this.locationMarker, this.continuousHeading)
    } else {
      const el = this.createArrowMarkerElement(this.continuousHeading ?? location.heading)
      this.locationMarker = new maplibregl.Marker({ element: el }).setLngLat(lngLat).addTo(this.map)
    }

    if (location.accuracy) {
      this.updateAccuracyCircle(lngLat, location.accuracy)
    }
  }

  private updateAccuracyCircle(center: [number, number], radius: number): void {
    if (!this.map || !(this.map as unknown as Record<string, unknown>).style) {
      return
    }
    const circleCoords = circleToGeoJSONCoords(center, radius)
    const geojson: GeoJSON.Feature = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [circleCoords] },
    }
    const existingSource = this.map.getSource(this.accuracyCircleSourceId)
    if (isGeoJSONSource(existingSource)) {
      existingSource.setData(geojson)
    } else {
      this.map.addSource(this.accuracyCircleSourceId, { type: 'geojson', data: geojson })
      this.map.addLayer({
        id: this.accuracyCircleLayerId,
        type: 'fill',
        source: this.accuracyCircleSourceId,
        paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.1 },
      })
      this.map.addLayer({
        id: this.accuracyCircleStrokeLayerId,
        type: 'line',
        source: this.accuracyCircleSourceId,
        paint: { 'line-color': '#2563eb', 'line-width': 1 },
      })
    }
  }

  panToCurrentLocation(): void {
    if (this.currentLocation && this.map) {
      this.map.easeTo({
        center: [this.currentLocation.lng, this.currentLocation.lat],
        duration: 500,
      })
    }
  }

  /**
   * Request an immediate GPS fix and process it through the normal pipeline
   * (marker, track point, persistence). Does NOT move the camera on its own.
   * Callers who want to pan the map should call `panToCurrentLocation()`
   * explicitly — the default behavior must never stomp on a plan's saved
   * camera target during initial map load.
   */
  async requestImmediateLocation(): Promise<void> {
    try {
      const location = await this.compassService.getCurrentPosition()
      log.debug(
        `📍 Immediate fix: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)} ±${location.accuracy}m`
      )
      this.handleLocationUpdate(location)
    } catch {
      log.debug('📍 Immediate fix failed, using cached location')
    }
  }

  /** Pan to current location without changing zoom — used during follow mode */
  followToCurrentLocation(): void {
    if (this.currentLocation && this.map) {
      this.map.easeTo({
        center: [this.currentLocation.lng, this.currentLocation.lat],
        duration: 300,
      })
    }
  }

  /**
   * React to screen visibility changes. Three target states:
   *
   *   A. Screen on (recording or not):
   *        GPS 5s interval, no HAL batching, HIGH accuracy.
   *   B. Screen off, recording:
   *        GPS 5s interval, 60s HAL batching, HIGH accuracy. The GNSS chip
   *        can duty-cycle between wakes without losing fixes; batched points
   *        arrive in bursts and are drained on screen-on via wake-flush.
   *   C. Screen off, NOT recording:
   *        Stop tracking entirely. Nothing the user cares about depends on
   *        knowing location in this state — the marker is invisible, no
   *        track is being captured. Foreground service stops, notification
   *        dismisses, GPS draws zero power. On screen-on we start fresh.
   *
   * HIGH_ACCURACY is the only fix quality we ever accept. BALANCED delivers
   * ~100m WiFi/cell positions that would corrupt recorded tracks.
   */
  async onScreenStateChanged(isVisible: boolean): Promise<void> {
    const displacement = LocationTrackingManager.DISPLACEMENT_MAP[this.callbacks.getTrackQuality()]
    const recording = this.callbacks.isRecording()

    if (isVisible) {
      // State A — screen on. The service lifecycle is owned by locationService
      // (demand-gated); here we only refresh request params and drain the
      // native buffer. Updating params on a stopped service is a harmless no-op.
      this.compassService.logDiagnostic(
        'GPS_PARAMS',
        `screen on recording=${recording} → priority=high interval=5s displacement=${displacement}m maxDelay=0`
      )
      void this.compassService.updateLocationParameters({
        interval: 5,
        priority: 'high',
        displacement,
        maxUpdateDelayMillis: 0,
      })

      // Drain anything the native buffer accumulated while we were hidden.
      // This is the sole delivery path for buffered points — the native
      // handleOnResume no longer drains, because notifyListeners can fire
      // before the WebView's JS engine is ready, silently dropping events.
      //
      // We drain twice: once immediately (gets most points), and once after
      // a short delay to catch any fixes that the HAL flush (triggered by
      // native handleOnResume) delivers to the file between our first drain
      // and its async completion.
      if (recording) {
        const count = await this.compassService.flushBufferedLocations()
        this.compassService.logDiagnostic('BUFFER', `first drain returned ${count} points`)

        // Second drain after HAL flush has time to deliver
        setTimeout(() => {
          void this.compassService.flushBufferedLocations().then(count2 => {
            if (count2 > 0) {
              this.compassService.logDiagnostic('BUFFER', `second drain returned ${count2} points`)
            }
          })
        }, 2000)
      }
      return
    }

    // Screen is off.
    if (recording) {
      // State B — keep tracking, reduce batching window.
      // maxDelay=15s is a compromise: short enough that points get written
      // to the file buffer frequently (surviving process death), but still
      // lets the GNSS chip duty-cycle slightly between wakes.
      this.compassService.logDiagnostic(
        'GPS_PARAMS',
        `screen off recording → priority=high interval=5s displacement=${displacement}m maxDelay=15000`
      )
      void this.compassService.updateLocationParameters({
        interval: 5,
        priority: 'high',
        displacement,
        maxUpdateDelayMillis: 15000,
      })
      return
    }

    // State C — screen off, not recording. Do nothing.
    // We intentionally do NOT stop location tracking here. The native
    // foreground service is a shared resource — stopping it from one
    // component's visibility handler kills GPS for ALL consumers (including
    // an active recording in another cairn-map instance). Let the service
    // idle; it will be stopped on component dispose or explicit user action.
    this.compassService.logDiagnostic('GPS_PARAMS', 'screen off idle → no action')
  }

  stopLocationTracking(): void {
    if (this.isLocationTracking) {
      void this.compassService.stopLocationTracking()
      this.isLocationTracking = false
    }
  }

  /** Last known position (for recording plan-matching / consumers). */
  getCurrentLocation(): { lat: number; lng: number } | undefined {
    return this.currentLocation
      ? { lat: this.currentLocation.lat, lng: this.currentLocation.lng }
      : undefined
  }

  /**
   * Detach the live map on component unmount: remove the marker, accuracy
   * layers, and camera listeners tied to the (about-to-be-destroyed) map.
   * Does NOT stop the GPS service — that lifecycle is owned by locationService,
   * so an active recording keeps capturing after the map view goes away.
   */
  detachMap(): void {
    if (this.map && this.boundPitchBearingHandler) {
      this.map.off('pitch', this.boundPitchBearingHandler)
      this.map.off('rotate', this.boundPitchBearingHandler)
    }

    if (this.locationMarker) {
      this.locationMarker.remove()
      this.locationMarker = undefined
    }

    // Clean up accuracy circle layers and source
    if (this.map) {
      if (this.map.getLayer(this.accuracyCircleStrokeLayerId)) {
        this.map.removeLayer(this.accuracyCircleStrokeLayerId)
      }
      if (this.map.getLayer(this.accuracyCircleLayerId)) {
        this.map.removeLayer(this.accuracyCircleLayerId)
      }
      if (this.map.getSource(this.accuracyCircleSourceId)) {
        this.map.removeSource(this.accuracyCircleSourceId)
      }
    }

    this.map = undefined
    this.continuousHeading = undefined
  }

  dispose(): void {
    this.stopLocationTracking()
    void this.compassService.stopTracking()
    this.detachMap()
    this.currentLocation = undefined
    this.deviceHeading = undefined
  }
}
